import React, { useEffect, useState } from 'react';
import {
	Grid,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Input from 'src/components/Input';

const INIT_FORM_DATA = {
	productId: '',
	productTitle: '',
	consumerPrice: '',
	price: '',
	precautions: '',
	sortingOrder: '',
	limitIssueQty: '',
	totalQuantity: '',
	isDelete: false,
};

const INIT_ERRORS = {
	price: '',
	precautions: '',
	sortingOrder: '',
	limitIssueQty: '',
	totalQuantity: '',
};

const ProductModal = ({ 
  isVisible, 
  onClose, 
	productModalData,
	loadingHandler,
	onAddProduct,
	onUpdateProduct
}) => {

	const theme = useTheme();
	const { product, existingData, orderSequence, targetSequence } = productModalData;
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

	const isAdd = existingData ? false : true;
	const [errors, setErrors] = useState(INIT_ERRORS);
	const [formData, setFormData] = useState(INIT_FORM_DATA);
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		// reset if product changed
		setHasError(false);
		setFormData(INIT_FORM_DATA);
		setErrors(INIT_ERRORS);

		if (product) {
			setFormData(prev => ({
				...prev,
				productId: product.id,
				productTitle: product.title,
				price: product.price,
				consumerPrice: product.consumerPrice,
				precautions: product.precautions || '',
			}));
		}

		if (orderSequence && orderSequence > 0) {
			setFormData(prev => ({
				...prev,
				sortingOrder: orderSequence,
			}));
		}

		if (existingData) {
			setFormData(prev => ({
				...prev,
				price: existingData.price,
				precautions: existingData.precautions,
				sortingOrder: existingData.sortingOrder,
				totalQuantity: existingData.totalQuantity || '0',
				limitIssueQty: existingData.limitIssueQty,
			}));
		}
	}, [productModalData]);  // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const keys = Object.keys(errors);
		const hasValue = keys.map(errKey => {
			if (formData[errKey] === '' || formData[errKey] === null) {
				return false;
			}
			if (errors[errKey] !== '') {
				return false;
			}
			return true;
		}).reduce((prev, curr) => (prev && curr), true);

		setHasError(!hasValue);
	}, [formData, errors]);

	const handleCloseModal = (e) => {
		e && e.stopPropagation();
	};

	const handelCancel = (e) => {
		e && e.stopPropagation();
		onClose();
	}

	const handelSubmit = async () => {
		loadingHandler(true);
		const items = Object.keys(formData);
	
		const validation = [];
		for (const item of items) {
			validation.push(new Promise(resolve => {
				if (!validateField(item, formData[item])) return resolve(false);
				resolve(!validateHasError(item, formData[item]));
			}));
		}

		const result = await Promise.all(validation);

		if (result.reduce((prev, curr) => (prev && curr), true)) {
			// pass validation
			if (isAdd) {
				onAddProduct(formData, targetSequence);
			} else {
				onUpdateProduct(formData, existingData.id);
			}
		} else {
			loadingHandler(false);
		}
	}

	const validateField = (name, value) => {
		if (!(name in errors)) {
			return true;
		}

		setErrors((prevState) => ({
			...prevState,
			[name]: value ? '' : 'This field is required.',
		}));
		if (!value) return false;

		if (name === 'price') {
			setErrors((prevState) => ({
				...prevState,
				[name]: formData.consumerPrice <= value ? 'Price should be less than consumer price' : '',
			}));
			if (formData.consumerPrice <= value) return false;
		}

		return true;
	};

	const handleInputChange = (event, type) => {
		const { id, value } = event.target;

		setFormData((prevState) => ({
			...prevState,
			[id]: value,
		}));

		if (type === 'number') {
			setErrors((prevState) => ({
				...prevState,
				[id]: value <= 0 ? 'Only accept positive numbers' : '',
			}));

			if (id === 'totalQuantity' && !isAdd && value > 0) {
				const existTotal = parseInt(existingData.totalQuantity) || 0;
				const existRemainQty = existTotal - (parseInt(existingData.soldQuantity) || 0);
				const val = parseInt(value, 10) || 0;
				const remainQty = (value > 0 ? val - existTotal: 0) + existRemainQty;
				
				setErrors((prevState) => ({
					...prevState,
					[id]: remainQty < 0 ? 'Number deducted from total quantity cannot be lesser than remain quantity' : '',
				}));
				if (remainQty < 0) return;
			}

			if (
				(id === 'limitIssueQty' && value > 0) ||
				(id === 'totalQuantity' && value > 0)
			) {
				let hasQtyError = false;
				if (id === 'limitIssueQty' && value > (parseInt(formData.totalQuantity) || 0)) hasQtyError = true;
				if (id === 'totalQuantity' && value < (parseInt(formData.limitIssueQty) || 0)) hasQtyError = true;

				setErrors((prevState) => ({
					...prevState,
					[id]: hasQtyError ? 'Limit issue quantity should not be greater than total quantity available' : '',
					[id === 'limitIssueQty' ? 'totalQuantity' : 'limitIssueQty']: ''
				}));
				if (hasQtyError) return;
			}

			if (value <= 0) return;
		}

		validateField(id, value);
	};

	const inputPropsHandlers = {
		onChangeBlur: handleInputChange,
	};

	const validateHasError = (name, value) => {
		if (!(name in errors)) return false;
		if (errors[name] !== '') return true;

		return !(value !== null && value !== '' && value !== undefined);
	};


	return (
		<Dialog
			open={isVisible}
			onClose={handleCloseModal}
			fullScreen={fullScreen}
			scroll="paper"
			maxWidth={'sm'}
		>
			<DialogTitle id="scroll-dialog-title" onClose={onClose}>
				{
					isAdd ? 
					'Add Products to Flash Sale' : 
					'Update Products'
				}
			</DialogTitle>
			<DialogContent dividers>
				<Box sx={{ flexGrow: 1 }}>
					<Grid container spacing={3} columns={12} direction="column">
						<Grid item md={7} xs={12}>
							<Box sx={{ flexGrow: 1 }}>
								<Grid container spacing={3}>
									<Grid container item md={12} xs={12}>
										<Input
											id="productId"
											label="Product ID"
											value={formData.productId}
											required={false}
											disabled
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											id="productTitle"
											label="Product Title"
											value={formData.productTitle}
											required={false}
											disabled
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											id="consumerPrice"
											label="Consumer Price"
											value={formData.consumerPrice}
											required={false}
											disabled
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="price"
											label="Price"
											value={formData.price}
											error={errors.price}
											type='number'
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="limitIssueQty"
											label="Limit Issue Quantity"
											value={formData.limitIssueQty}
											error={errors.limitIssueQty}
											type='number'
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="totalQuantity"
											label="Total Quantity Available"
											value={formData.totalQuantity}
											error={errors.totalQuantity}
											type='number'
										/>
									</Grid>
									<Grid container item md={12} xs={12}>
										<Input
											{...inputPropsHandlers}
											id="precautions"
											label="Precautions"
											value={formData.precautions}
											error={errors.precautions}
											type='multi'
										/>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handelCancel}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handelSubmit}
					disabled={hasError}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProductModal;
